import React, { useMemo, useState } from 'react';
import { Accent, ITabHeaderChangeArgs, TagTabHeader, TagTabHeaders } from '@tag/tag-components-react-v3';

import TabContent from './TabContent';
import { useAddQueryToUrl } from './useAddQueryToUrl';
import { browserHistory } from '@/routing/browserHistory';

// no-cache - mount tab in DOM each time from scratch selecting it
// after-select - mount tab in DOM when it selected first time and don't remove form DOM anymore
// on-init - mount all tab in DOM from the beginning
type TabsCacheMode = 'no-cache' | 'after-select' | 'on-init';

interface ITagTabs {
  tabs: TabType[];
  contentClassName?: string;
  headerContainerStyle?: React.CSSProperties;
  headersContainerStyle?: React.CSSProperties;
  accent?: Accent;
  disabled?: boolean;
  activeTabKey?: string;
  onTabChange?: (e: CustomEvent<ITabHeaderChangeArgs>) => void;
  addQueryToUrl?: boolean;
  urlQueryParamName?: string;
  cacheMode?: TabsCacheMode;
}

export type TabType = {
  title: string;
  name: string;
  ContentComponent?: React.FC;
};

const basicContainerStyle = {
  justifyContent: 'center',
  margin: 0,
  padding: '10px 15px'
};

export default function TagTabs(props: ITagTabs) {
  const {
    tabs,
    contentClassName,
    headerContainerStyle,
    headersContainerStyle,
    accent = 'sunflower',
    disabled,
    activeTabKey = tabs[0].name,
    onTabChange,
    addQueryToUrl = true,
    urlQueryParamName = 'tab',
    cacheMode = 'on-init'
  } = props;

  const initiallyActiveTab = useMemo(() => {
    return tabs.find((el) => el.name === activeTabKey) ?? tabs[0];
  }, [activeTabKey, tabs]);

  const { getLinkWithQuery } = useAddQueryToUrl();
  const [activeTab, setActiveTab] = useState<TabType>(initiallyActiveTab);
  const [mountedTab, setMountedTab] = useState<TabType[]>(() => {
    return cacheMode === 'on-init' ? tabs : [initiallyActiveTab];
  });

  const tabsToRenderContent = cacheMode === 'on-init' ? tabs : mountedTab;

  const onTabHeaderChange = (event: CustomEvent<ITabHeaderChangeArgs>) => {
    const tabName = event?.detail?.name;
    const tab = tabs.find((el) => el.name === tabName)!;
    setActiveTab(tab);

    if (cacheMode === 'after-select' && !mountedTab.some((el) => el.name === tabName)) {
      setMountedTab((prevState) => [...prevState, tab]);
    }

    if (cacheMode === 'no-cache') {
      setMountedTab([tab]);
    }

    if (addQueryToUrl) {
      const queryParams = {
        [urlQueryParamName]: tabName,
      };
      const routePath = getLinkWithQuery(queryParams);
      browserHistory.push(routePath);
    }

    onTabChange?.(event);
  };

  return (
    <>
      <TagTabHeaders
        accent={accent}
        onTabHeaderChange={onTabHeaderChange}
        selectedTab={activeTabKey}
        containerStyle={headersContainerStyle}
        style={{ display: 'flex' }}
      >
        {tabs.map((el: TabType) => (
          <TagTabHeader
            key={el.name}
            name={el.name}
            containerStyle={headerContainerStyle ?? basicContainerStyle}
            disabled={disabled}
            data-testid="tag-tab-header"
          >
            {el.title}
          </TagTabHeader>
        ))}
      </TagTabHeaders>

      <TabContent
        tabs={tabsToRenderContent}
        activeTab={activeTab}
        contentClassName={contentClassName}
      />
    </>
  );
}
